import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { MapPin, RefreshCcw, ExternalLink, User as UserIcon } from "lucide-react";
import LiveMap from "./LiveMap";

function timeAgo(iso) {
  if (!iso) return "Never";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function statusOf(iso) {
  if (!iso) return { tone: "grey", label: "Never shared" };
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 5) return { tone: "green", label: "Live" };
  if (mins < 30) return { tone: "yellow", label: "Recent" };
  return { tone: "grey", label: "Stale" };
}

export default function LiveLocation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations/salesmen");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30 * 1000); // auto-refresh every 30s
    const tickIv = setInterval(() => setTick((t) => t + 1), 15 * 1000);
    return () => {
      clearInterval(iv);
      clearInterval(tickIv);
    };
  }, []);

  const sorted = [...items].sort((a, b) => {
    const aAt = a.last_location?.at || "";
    const bAt = b.last_location?.at || "";
    return bAt.localeCompare(aAt);
  });

  const liveCount = items.filter((s) => {
    const at = s.last_location?.at;
    return at && (Date.now() - new Date(at).getTime()) / 60000 < 5;
  }).length;

  return (
    <div className="space-y-5" data-testid="admin-live-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Field ops</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Live location
          </h1>
          <p className="text-[color:var(--muted)] mt-1">
            Where is each salesman right now? Auto-refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[color:var(--muted)]">
            <span className="inline-block h-2 w-2 rounded-full bg-[color:var(--moss)] mr-1" />
            {liveCount} live
          </span>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="live-refresh-button">
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <LiveMap salesmen={items} />

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((s) => {
          const loc = s.last_location;
          const status = statusOf(loc?.at);
          const toneMap = {
            green: "bg-[color:var(--moss)]/15 text-[color:var(--moss)] border-[color:var(--moss)]/30",
            yellow: "bg-[color:var(--mustard)]/20 text-[#9A742E] border-[color:var(--mustard)]/40",
            grey: "bg-[color:var(--surface-2)] text-[color:var(--muted)] border-[color:var(--border)]",
          };
          return (
            <div
              key={s.id}
              className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow"
              data-testid={`live-card-${s.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-[color:var(--moss)]/10 text-[color:var(--moss)] flex items-center justify-center shrink-0">
                  <UserIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold truncate">{s.name}</div>
                    {s.active === false && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--primary)]/10 text-[color:var(--primary)] uppercase tracking-wide">
                        Suspended
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--muted)] truncate">{s.email}</div>
                </div>
                <span
                  className={`text-[10px] px-2 py-1 rounded-full border ${toneMap[status.tone]}`}
                >
                  {status.label}
                </span>
              </div>

              <div className="mt-4 border-t border-[color:var(--border)] pt-3">
                {loc ? (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-[color:var(--muted)]" />
                      <span className="font-mono tabular-nums">
                        {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                      </span>
                    </div>
                    <div className="text-xs text-[color:var(--muted)] mt-1">
                      Updated {timeAgo(loc.at)}
                      {loc.accuracy ? ` · ±${Math.round(loc.accuracy)}m` : ""}
                    </div>
                    <a
                      href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 mt-3 text-sm text-[color:var(--primary)] hover:underline"
                      data-testid={`live-map-link-${s.id}`}
                    >
                      Open in Google Maps <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                ) : (
                  <div className="text-sm text-[color:var(--muted)] flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> No location shared yet
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && !loading && (
          <div className="col-span-full text-center py-12 text-[color:var(--muted)] rounded-xl border border-dashed border-[color:var(--border)]">
            No salesmen in the system yet.
          </div>
        )}
      </div>

      <div className="text-xs text-[color:var(--muted)] flex items-start gap-2 rounded-lg bg-[color:var(--surface-2)] p-3">
        <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <b>How this works:</b> the mobile app pings each salesman's GPS every 90 seconds while the app is open, and whenever they check in at a store. If the app is closed or location permission is denied, the last known position is shown. Colour dot shows freshness: green = &lt;5 min, yellow = &lt;30 min, grey = stale.
        </div>
      </div>
    </div>
  );
}
